/**
 * @file ScrollArea 自定义滚动区域
 * @description 隐藏系统滚动条，用悬浮滑块叠在内容之上，不占用滚动区域尺寸。
 *
 * @whenToUse 固定高度/宽度容器内的长列表、下拉面板、侧栏、Dialog 内容区。
 * @whenNotToUse
 * 1. 整页文档流滚动（可后续用布局级 ScrollArea 包裹）。
 * 2. 不需要滚动的静态块。
 */

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type HTMLAttributes,
  type ReactNode,
  type Ref,
  type UIEventHandler,
} from 'react'
import { cn } from '@/lib/cn.ts'

export type ScrollAreaShowOn = 'hover' | 'always' | 'never'

export type ScrollAreaProps = HTMLAttributes<HTMLDivElement> & {
  children: ReactNode
  /** 滚动方向 */
  orientation?: 'vertical' | 'horizontal' | 'both'
  /**
   * 滚动条显示时机
   * - hover：鼠标进入滚动区时显示（默认）
   * - always：只要可滚动就显示
   * - never：永不显示自定义条（仍可滚轮滚动）
   */
  showOn?: ScrollAreaShowOn
  /** 视口（实际滚动元素）className */
  viewportClassName?: string
  /** 视口 DOM ref（用于外部监听尺寸 / sticky 联动） */
  viewportRef?: Ref<HTMLDivElement>
  /** 视口滚动回调 */
  onViewportScroll?: UIEventHandler<HTMLDivElement>
}

/**
 * 同时写入内部 ref 与外部传入的 ref。
 * @param el - 视口 DOM
 * @param inner - ScrollArea 内部 ref
 * @param outer - 调用方传入的 ref
 */
function assignViewportRef(
  el: HTMLDivElement | null,
  inner: { current: HTMLDivElement | null },
  outer: Ref<HTMLDivElement> | undefined,
) {
  inner.current = el
  if (!outer) return
  if (typeof outer === 'function') {
    outer(el)
    return
  }
  outer.current = el
}

type AxisMetrics = {
  thumbSize: number
  thumbOffset: number
  needed: boolean
}

const THUMB_MIN = 24
const TRACK_CROSS = 9

/**
 * 自定义滚动容器：原生 overflow 滚动 + 悬浮滑块（不占布局空间）
 */
export function ScrollArea({
  children,
  className,
  orientation = 'vertical',
  showOn = 'hover',
  viewportClassName,
  viewportRef: viewportRefProp,
  onViewportScroll,
  ...rest
}: ScrollAreaProps) {
  const viewportRef = useRef<HTMLDivElement>(null)
  const [hovering, setHovering] = useState(false)
  const [vMetrics, setVMetrics] = useState<AxisMetrics>({
    thumbSize: 0,
    thumbOffset: 0,
    needed: false,
  })
  const [hMetrics, setHMetrics] = useState<AxisMetrics>({
    thumbSize: 0,
    thumbOffset: 0,
    needed: false,
  })
  const dragRef = useRef<{
    axis: 'v' | 'h'
    startPos: number
    startScroll: number
  } | null>(null)

  const enableV = orientation === 'vertical' || orientation === 'both'
  const enableH = orientation === 'horizontal' || orientation === 'both'

  const updateMetrics = useCallback(() => {
    const el = viewportRef.current
    if (!el) return

    if (enableV) {
      const { clientHeight, scrollHeight, scrollTop } = el
      const needed = scrollHeight > clientHeight + 1
      const ratio = clientHeight / scrollHeight
      const thumbSize = needed
        ? Math.max(THUMB_MIN, Math.round(clientHeight * ratio))
        : 0
      const maxOffset = clientHeight - thumbSize
      const thumbOffset = needed
        ? Math.round((scrollTop / (scrollHeight - clientHeight)) * maxOffset) ||
          0
        : 0
      setVMetrics({ thumbSize, thumbOffset, needed })
    }

    if (enableH) {
      const { clientWidth, scrollWidth, scrollLeft } = el
      const needed = scrollWidth > clientWidth + 1
      const ratio = clientWidth / scrollWidth
      const thumbSize = needed
        ? Math.max(THUMB_MIN, Math.round(clientWidth * ratio))
        : 0
      const maxOffset = clientWidth - thumbSize
      const thumbOffset = needed
        ? Math.round((scrollLeft / (scrollWidth - clientWidth)) * maxOffset) ||
          0
        : 0
      setHMetrics({ thumbSize, thumbOffset, needed })
    }
  }, [enableH, enableV])

  useEffect(() => {
    const el = viewportRef.current
    if (!el) return

    updateMetrics()
    el.addEventListener('scroll', updateMetrics, { passive: true })

    const ro = new ResizeObserver(updateMetrics)
    ro.observe(el)
    if (el.firstElementChild) ro.observe(el.firstElementChild)

    return () => {
      el.removeEventListener('scroll', updateMetrics)
      ro.disconnect()
    }
  }, [updateMetrics, children])

  useEffect(() => {
    const onMove = (event: PointerEvent) => {
      const drag = dragRef.current
      const el = viewportRef.current
      if (!drag || !el) return

      if (drag.axis === 'v') {
        const maxScroll = el.scrollHeight - el.clientHeight
        const maxOffset = el.clientHeight - vMetrics.thumbSize
        const delta = event.clientY - drag.startPos
        const next =
          drag.startScroll + (delta / Math.max(maxOffset, 1)) * maxScroll
        el.scrollTop = next
      } else {
        const maxScroll = el.scrollWidth - el.clientWidth
        const maxOffset = el.clientWidth - hMetrics.thumbSize
        const delta = event.clientX - drag.startPos
        const next =
          drag.startScroll + (delta / Math.max(maxOffset, 1)) * maxScroll
        el.scrollLeft = next
      }
    }

    const onUp = () => {
      dragRef.current = null
    }

    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    return () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
    }
  }, [hMetrics.thumbSize, vMetrics.thumbSize])

  const showBar =
    showOn === 'always' || (showOn === 'hover' && hovering)

  return (
    <div
      className={cn('relative min-h-0 min-w-0 overflow-hidden', className)}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      {...rest}
    >
      <div
        ref={(el) => assignViewportRef(el, viewportRef, viewportRefProp)}
        className={cn(
          'h-full w-full overscroll-contain',
          enableV && 'overflow-y-auto',
          enableH && 'overflow-x-auto',
          !enableV && 'overflow-y-hidden',
          !enableH && 'overflow-x-hidden',
          viewportClassName,
        )}
        onScroll={onViewportScroll}
      >
        {children}
      </div>

      {enableV && vMetrics.needed ? (
        <div
          className={cn(
            'pointer-events-none absolute top-0 right-0 bottom-0 z-10 transition-opacity duration-200',
            showBar ? 'opacity-100' : 'opacity-0',
          )}
          style={{ width: TRACK_CROSS }}
          aria-hidden
        >
          <div
            className="pointer-events-auto absolute right-0 left-0.5 rounded-full bg-muted/40 transition-colors duration-200 hover:bg-muted active:bg-muted"
            style={{
              height: vMetrics.thumbSize,
              transform: `translateY(${vMetrics.thumbOffset}px)`,
            }}
            onPointerDown={(event) => {
              event.preventDefault()
              event.stopPropagation()
              const el = viewportRef.current
              if (!el) return
              dragRef.current = {
                axis: 'v',
                startPos: event.clientY,
                startScroll: el.scrollTop,
              }
            }}
          />
        </div>
      ) : null}

      {enableH && hMetrics.needed ? (
        <div
          className={cn(
            'pointer-events-none absolute right-0 bottom-0 left-0 z-10 transition-opacity duration-200',
            showBar ? 'opacity-100' : 'opacity-0',
          )}
          style={{ height: TRACK_CROSS }}
          aria-hidden
        >
          <div
            className="pointer-events-auto absolute top-0.5 bottom-0.5 rounded-full bg-muted/40 transition-colors duration-200 hover:bg-muted active:bg-muted"
            style={{
              width: hMetrics.thumbSize,
              transform: `translateX(${hMetrics.thumbOffset}px)`,
            }}
            onPointerDown={(event) => {
              event.preventDefault()
              event.stopPropagation()
              const el = viewportRef.current
              if (!el) return
              dragRef.current = {
                axis: 'h',
                startPos: event.clientX,
                startScroll: el.scrollLeft,
              }
            }}
          />
        </div>
      ) : null}
    </div>
  )
}
